import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { BgmTrack } from '../data/bgmList';
import { useAudioStore } from '../stores/useAudioStore';


export function useBgm(bgmList: BgmTrack[]) {
  const [isBgmPlaying, setIsBgmPlaying] = useState(false);
  const [currentBgmIndex, setCurrentBgmIndex] = useState<number | null>(null);
  const [bgmProgress, setBgmProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<number | null>(null);
  
  const { audioSettings, bgmUnlocks } = useAudioStore();
  
  // BGMリストにアンロック状態を反映
  const localBgmList = useMemo(() => bgmList.map(track => ({
    ...track,
    unlocked: bgmUnlocks[track.id] || track.unlocked,
  })), [bgmList, bgmUnlocks]);

  // BGM再生
  const playBgm = useCallback((index: number) => {
    // 既存のBGMを停止
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    // アンロック状態と音量は実行時に最新値を取得（クロージャーの古い値を避ける）
    const { audioSettings: settings, bgmUnlocks: unlocks } = useAudioStore.getState();
    const track = bgmList[index];
    if (!track || !(unlocks[track.id] || track.unlocked)) return;

    // 新しいBGMを再生（play()のresolve前でも停止・差し替えできるよう先にrefへ登録）
    const audio = new Audio(`/assets/audio/bgm/${track.file}`);
    audio.loop = true;
    audio.volume = settings.bgmVolume * settings.masterVolume; // マスターボリュームを適用
    audioRef.current = audio;
    setCurrentBgmIndex(index);
    setIsBgmPlaying(true);
    setBgmProgress(0);

    audio.play().then(() => {
      // resolve前に停止・別曲へ切り替え済みなら破棄
      if (audioRef.current !== audio) {
        audio.pause();
        return;
      }

      // 進捗更新
      progressIntervalRef.current = window.setInterval(() => {
        if (audio.duration > 0) {
          setBgmProgress(audio.currentTime / audio.duration);
        }
      }, 500);
    }).catch((error) => {
      console.error('BGM再生エラー:', error);
      if (audioRef.current === audio) {
        audioRef.current = null;
        setIsBgmPlaying(false);
        setCurrentBgmIndex(null);
        setBgmProgress(0);
      }
    });
  }, [bgmList]);

  // BGM停止
  const stopBgm = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setIsBgmPlaying(false);
    setCurrentBgmIndex(null);
    setBgmProgress(0);
  }, []);

  // 音量設定が変更されたら適用
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = audioSettings.bgmVolume * audioSettings.masterVolume;
    }
  }, [audioSettings.bgmVolume, audioSettings.masterVolume]);

  // クリーンアップ（refをnullにすることでpending中のplay()も破棄される）
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  return {
    isBgmPlaying,
    currentBgmIndex,
    bgmProgress,
    bgmList: localBgmList,
    playBgm,
    stopBgm,
  };
}